import React, { useEffect, useState } from 'react';
import { setSupportSession } from '../../utils/supportSession';
import { Empty, ErrorText, Panel, useChrome } from './ceoChrome';

export default function SupportViewPanel({ colors, narrow, data, error, onAction, busy }) {
  const chrome = useChrome(colors, narrow);
  const [email, setEmail] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [snapshot, setSnapshot] = useState(data && data.snapshot ? data.snapshot : null);
  const [localError, setLocalError] = useState('');
  const agents = (data && data.agents) || [];

  useEffect(() => {
    if (data && data.snapshot) setSnapshot(data.snapshot);
    if (data && data.sessionId) setSessionId(data.sessionId);
  }, [data]);

  useEffect(() => {
    setSupportSession(sessionId);
    return () => setSupportSession('');
  }, [sessionId]);

  async function run(body) {
    if (!onAction) return null;
    setLocalError('');
    try {
      return await onAction(body);
    } catch (err) {
      setLocalError(err.message || 'Could not open the support view');
      return null;
    }
  }

  async function start(event) {
    event.preventDefault();
    const result = await run({ action: 'start', email: email.trim() });
    if (!result) return;
    setSessionId(result.sessionId || '');
    setSnapshot(result.snapshot || null);
  }

  async function end() {
    const result = await run({ action: 'end', sessionId });
    if (!result) return;
    setSessionId('');
    setSnapshot(null);
    setSupportSession('');
  }

  const view = snapshot;

  return (
    <div style={chrome.page} data-testid="ceo-support">
      <div>
        <h2 style={chrome.title}>Support view</h2>
        <p style={chrome.sub}>Read only, and only after the agent allows it. Homeowner names, emails, phones, street addresses, notes, documents, and AI chat stay on the server.</p>
      </div>
      {sessionId ? (
        <div role="status" style={{ ...chrome.panel, background: '#fff6df', borderColor: '#f3d48a' }}>
          Support view is open. Nothing here can be edited, sent, or exported.
        </div>
      ) : null}
      {error || localError ? <ErrorText>{error || localError}</ErrorText> : null}
      <form onSubmit={start} style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', gap: 8 }}>
        <input
          aria-label="Agent email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Agent email"
          style={{ ...chrome.field, flex: 1 }}
        />
        <button type="submit" style={chrome.button} disabled={busy || !email.trim()}>View account</button>
      </form>
      {agents.length > 0 && !view ? (
        <Panel chrome={chrome} title="Agents">
          {agents.slice(0, narrow ? 6 : 12).map((agent) => (
            <button
              key={agent.id}
              type="button"
              onClick={() => setEmail(agent.email || '')}
              style={{ ...chrome.quiet, width: '100%', textAlign: 'left', marginTop: 8, display: 'flex', justifyContent: 'space-between', gap: 12 }}
            >
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{agent.name || agent.email}</span>
              <span style={{ color: chrome.secondary, fontWeight: 400 }}>{agent.grantActive ? 'Allowed' : 'No consent'}</span>
            </button>
          ))}
        </Panel>
      ) : null}
      {view ? (
        <Panel chrome={chrome} title="Account">
          <div data-readonly="true">
            <div style={{ fontSize: 20, fontWeight: 600 }}>{view.agent && view.agent.name}</div>
            <div style={{ color: chrome.secondary, marginTop: 4 }}>{view.agent && view.agent.email}</div>
            <div style={{ color: chrome.secondary, marginTop: 4 }}>{view.agent && view.agent.plan} · {view.agent && view.agent.status}</div>
            <div style={{ marginTop: 16, fontWeight: 600 }}>Homeowners</div>
            {(view.homeowners || []).length === 0 ? <Empty chrome={chrome}>No homeowners.</Empty> : null}
            {(view.homeowners || []).map((homeowner) => (
              <div key={homeowner.label} style={{ padding: '12px 0', borderBottom: `1px solid ${chrome.line}` }} data-testid="redacted-homeowner">
                <div style={{ fontWeight: 600 }}>{homeowner.label}</div>
                <div style={{ color: chrome.secondary }}>{homeowner.city || 'City not on file'}</div>
              </div>
            ))}
            <div style={{ color: chrome.secondary, marginTop: 12 }}>
              {view.tasksCompleted} tasks completed · {view.projectsSent} projects · {view.documents} documents
            </div>
          </div>
          <button type="button" style={{ ...chrome.quiet, marginTop: 16 }} onClick={end}>End support view</button>
        </Panel>
      ) : null}
    </div>
  );
}
